/* WizardSlidePreviewAndNav — HTML preview of the current wizard slide,
   sub-slide preview stack, "Change this slide's design" gallery entry
   point, "Play deck" launcher, and Back/Next controls.

   Extracted from PitchEditor.jsx during the iter26 refactor so the parent
   page stops being a monolith. This component is intentionally self-
   contained (all state that lives outside it — deck, active slide,
   navigation — flows in as props).
*/

import React, { useRef, useState } from "react";
import { PlayCircle, Image as ImageIcon, Sparkle, CaretDown, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import SlidePreviewBody from "@/components/SlidePreviewBody";
import ImproveSlideDesign from "@/components/ImproveSlideDesign";
import PlayDeckModal from "@/components/PlayDeckModal";
import WizardFooterNav from "@/components/WizardFooterNav";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/apiError";

// Local palette mirrors PitchEditor's C.* — this component is small so
// duplicating three values is cheaper than plumbing a theme provider.
const TEAL_SOFT = "#7BD3CE";
const ORANGE = "#F59E0B";

// Shared "↳" glyph size for anything that references a sub-slide (this
// preview header AND the wizard step-pill chip in WizardChrome). Keeps
// visual parity — the two used to be 10px vs 8px which the testing agent
// flagged in iter25.
export const SUB_SLIDE_ARROW_SIZE = 9;

export default function WizardSlidePreviewAndNav({ deck, setDeck, slide, onBack, onNext, onOpenExtra, onDeleteExtra, onUploadPhoto }) {
  const [playOpen, setPlayOpen] = useState(false);
  // Accordion state — only one sub-slide's actions can be expanded at a
  // time so the previews section stops feeling like a wall of stacked
  // editors (iter46 UX cleanup).
  const [openActionsFor, setOpenActionsFor] = useState(null);
  if (!slide) return null;
  const bullets = (slide.content || "")
    .split("\n")
    .map((l) => l.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim())
    .filter((l) => l.length > 0);
  const primary = deck.brand_color || "#0A8B85";
  const layout = ((slide.design_prefs || {}).layout || "").trim();
  const isFirst = slide.num === 1;
  const isLast = slide.num === 12;
  const extras = slide.extras || [];

  return (
    <div className="mt-10" data-testid="wizard-slide-preview-wrap">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] mb-3 flex items-center gap-2" style={{ color: TEAL_SOFT }}>
        <span>Preview this slide</span>
        {layout && (
          <span className="text-[9px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded"
            style={{ backgroundColor: "rgba(245, 158, 11, 0.16)", color: ORANGE, border: "1px solid rgba(245, 158, 11, 0.35)" }}
            data-testid="wizard-preview-layout-badge">
            Custom · {layout.split("(")[0].trim()}
          </span>
        )}
        <button
          onClick={() => setPlayOpen(true)}
          data-testid="play-deck-open-btn"
          title="Play the whole deck as a slideshow (arrow keys navigate, Esc closes)"
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-[0.18em] transition-all"
          style={{
            backgroundColor: "rgba(245, 158, 11, 0.14)",
            color: ORANGE,
            border: "1px solid rgba(245, 158, 11, 0.35)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(245, 158, 11, 0.26)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(245, 158, 11, 0.14)"; }}
        >
          <PlayCircle size={12} weight="fill" /> Play deck
        </button>
      </div>
      <div className="rounded-xl overflow-hidden border shadow-2xl"
        style={{ borderColor: "rgba(255,255,255,0.08)", aspectRatio: "16 / 9", backgroundColor: "#FFFFFF" }}
        data-testid="wizard-slide-preview">
        <SlidePreviewBody deck={deck} slide={slide} bullets={bullets} primary={primary} layout={layout} onUploadPhoto={onUploadPhoto} />
      </div>
      <div className="text-[11px] text-white/45 mt-2 italic">
        {layout
          ? "Preview mirrors your chosen layout. The exported .pptx uses the same V2 template with full brand assets."
          : "This is a quick web preview — your final .pptx renders with full V2 templates, brand colours and infographics."}
      </div>

      {/* Change-design button for the MAIN slide — anchored directly below
          its own preview so founders don't have to scroll past the sub-slide
          stack to reach it. The equivalent button for each sub-slide lives
          under its own preview card further down.

          Iter53/55/57/58/59/60/63: Cover & Snapshot (1), Market Size (4),
          Business Model (5), Team (9), Financial Projections (10),
          Capital Structure & The Ask (11), and Thank You (12) are all
          locked to canonical layouts, so the "Change this slide's design"
          entry is hidden. */}
      {slide.num !== 1 && slide.num !== 4 && slide.num !== 5 && slide.num !== 8 && slide.num !== 9 && slide.num !== 10 && slide.num !== 11 && slide.num !== 12 && (
      <ImproveSlideDesign
        deckId={deck.deck_id}
        slideNum={slide.num}
        slideTitle={slide.title}
        slideImages={slide.images || []}
        onAutoPhotoGenerated={(img) => {
          // Optimistically add the new photo to the slide state so the
          // preview reflects it immediately. Backend has already persisted
          // + de-duped any prior auto-generated photo — we mirror that
          // policy here on the client.
          setDeck((d) => ({
            ...d,
            slides: d.slides.map((s) => s.num !== slide.num ? s : {
              ...s,
              images: [
                ...((s.images || []).filter((im) => im?.source !== "auto_generated")),
                img,
              ],
            }),
          }));
        }}
      />
      )}

      {/* Sub-slide previews — one 16:9 card per extra attached to this slide.
          Layout is inherited from the parent (via backend iter23) so photo
          + illustrated layouts render identically across the sequence.
          Clicking a card opens that sub-slide for editing (bypasses the
          debounced auto-save's stale-closure trap by passing the extraId
          directly to switchSlide). */}
      {extras.length > 0 && (
        <div className="mt-8" data-testid="wizard-subslide-preview-stack">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] mb-3 flex items-center gap-2" style={{ color: TEAL_SOFT }}>
            <span>Sub-slide preview{extras.length === 1 ? "" : "s"}</span>
            <span className="text-white/40 normal-case tracking-normal">·</span>
            <span className="text-white/50 normal-case tracking-normal text-[10.5px]">
              {extras.length === 1 ? "1 continuation" : `${extras.length} continuations`} of this slide
            </span>
          </div>
          <div className="space-y-5">
            {extras.map((ex, i) => {
              const exBullets = (ex.content || "")
                .split("\n")
                .map((l) => l.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim())
                .filter((l) => l.length > 0);
              const exLayout = ((ex.design_prefs || slide.design_prefs || {}).layout || "").trim();
              const parentLayout = (slide.design_prefs?.layout || "").trim();
              const layoutIsCustom = !!ex.design_prefs?.layout && ex.design_prefs.layout !== parentLayout;
              // Prefer the sub-slide's OWN images. Fall back to the parent's
              // images so the layout still renders correctly for founders
              // who haven't uploaded a distinct sub-slide photo yet.
              const exImages = (ex.images && ex.images.length > 0)
                ? ex.images
                : (slide.images || []);
              const hasOwnPhoto = (ex.images || []).length > 0;
              const exSlideMirror = {
                ...slide,
                title: ex.title || `${slide.title} (continued)`,
                content: ex.content || "",
                design_prefs: ex.design_prefs || slide.design_prefs || {},
                images: exImages,
              };
              const actionsOpen = openActionsFor === ex.id;
              return (
                <div key={ex.id} data-testid={`wizard-subslide-preview-${i}`}>
                  {/* Single-line clickable header — no separate Edit button
                      (the whole header + card is now the click target).
                      A subtle trash icon on the far right lets founders
                      delete a sub-slide directly from the wizard (previously
                      only accessible via the classic-mode sidebar). */}
                  <div className="flex items-center gap-2 mb-2" data-testid={`wizard-subslide-header-${i}`}>
                    <button
                      type="button"
                      onClick={() => onOpenExtra?.(ex.id)}
                      className="flex-1 min-w-0 flex items-center gap-2 text-left group"
                    >
                      <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] flex items-center gap-2" style={{ color: ORANGE }}>
                        <span style={{ fontSize: SUB_SLIDE_ARROW_SIZE }}>↳</span>
                        <span>Sub-slide {i + 1}</span>
                      </span>
                      <span className="text-white/35 normal-case tracking-normal">·</span>
                      <span className="text-white/85 normal-case tracking-normal text-[11.5px] font-semibold flex-1 min-w-0 truncate">
                        {ex.title || `${slide.title} (continued)`}
                      </span>
                      <span className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-white/40 group-hover:text-white/70 transition-colors">
                        Edit ↗
                      </span>
                    </button>
                    {onDeleteExtra && (
                      <button
                        type="button"
                        onClick={(e) => onDeleteExtra(ex.id, e)}
                        data-testid={`wizard-subslide-delete-${i}`}
                        title="Delete this sub-slide"
                        aria-label={`Delete sub-slide ${i + 1}`}
                        className="w-7 h-7 grid place-items-center rounded-md text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    )}
                  </div>
                  {/* Full 16:9 preview card — click to jump into the sub-slide editor. */}
                  <button
                    type="button"
                    onClick={() => onOpenExtra?.(ex.id)}
                    className="block w-full rounded-xl overflow-hidden border shadow-2xl cursor-pointer text-left"
                    style={{ borderColor: "rgba(255,255,255,0.08)", aspectRatio: "16 / 9", backgroundColor: "#FFFFFF" }}
                    title="Click to edit this sub-slide"
                    data-testid={`wizard-subslide-card-${i}`}
                  >
                    <SlidePreviewBody
                      deck={deck}
                      slide={exSlideMirror}
                      bullets={exBullets}
                      primary={primary}
                      layout={exLayout}
                    />
                  </button>
                  {/* One-line meta chip strip + single Actions ▾ pill.
                      Collapses the previous "photo status caption + full
                      change-design gallery" wall into a single row that
                      expands on demand (iter46 UX cleanup). */}
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: hasOwnPhoto ? "rgba(10,139,133,0.14)" : "rgba(255,255,255,0.06)",
                        color: hasOwnPhoto ? TEAL_SOFT : "rgba(255,255,255,0.55)",
                        border: `1px solid ${hasOwnPhoto ? "rgba(10,139,133,0.3)" : "rgba(255,255,255,0.1)"}`,
                      }}
                      data-testid={`wizard-subslide-photo-chip-${i}`}
                    >
                      📷 {hasOwnPhoto ? "Custom photo" : "Inherits from parent"}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: layoutIsCustom ? "rgba(245,158,11,0.14)" : "rgba(255,255,255,0.06)",
                        color: layoutIsCustom ? ORANGE : "rgba(255,255,255,0.55)",
                        border: `1px solid ${layoutIsCustom ? "rgba(245,158,11,0.3)" : "rgba(255,255,255,0.1)"}`,
                        maxWidth: "60%",
                      }}
                      data-testid={`wizard-subslide-layout-chip-${i}`}
                    >
                      🎨 <span className="truncate">{layoutIsCustom ? `Custom · ${ex.design_prefs.layout.split("(")[0].trim()}` : "Inherits layout"}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setOpenActionsFor(actionsOpen ? null : ex.id)}
                      className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full transition-colors"
                      style={{
                        backgroundColor: actionsOpen ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.06)",
                        color: "rgba(255,255,255,0.85)",
                        border: "1px solid rgba(255,255,255,0.14)",
                      }}
                      data-testid={`wizard-subslide-actions-toggle-${i}`}
                      aria-expanded={actionsOpen}
                    >
                      Actions
                      <CaretDown size={9} weight="bold" style={{ transform: actionsOpen ? "rotate(180deg)" : "none", transition: "transform 0.18s" }} />
                    </button>
                  </div>
                  {actionsOpen && (
                    <div
                      className="mt-3 space-y-3 rounded-lg p-3 border"
                      style={{ backgroundColor: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}
                      data-testid={`wizard-subslide-actions-body-${i}`}
                    >
                      <SubSlidePhotoAction
                        deckId={deck.deck_id}
                        slideNum={slide.num}
                        extraId={ex.id}
                        hasOwnPhoto={hasOwnPhoto}
                        setDeck={setDeck}
                      />
                      <ImproveSlideDesign
                        deckId={deck.deck_id}
                        slideNum={slide.num}
                        extraId={ex.id}
                        slideTitle={ex.title || `${slide.title} (continued)`}
                        slideImages={ex.images || []}
                        onAutoPhotoGenerated={(img) => {
                          setDeck((d) => ({
                            ...d,
                            slides: d.slides.map((s) => s.num !== slide.num ? s : {
                              ...s,
                              extras: (s.extras || []).map((x) => x.id !== ex.id ? x : {
                                ...x,
                                images: [
                                  ...((x.images || []).filter((im) => im?.source !== "auto_generated")),
                                  img,
                                ],
                              }),
                            }),
                          }));
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <WizardFooterNav
        onBack={onBack}
        onNext={onNext}
        backLabel={isFirst ? "Back to Brand & style" : "Back"}
        nextLabel={isLast ? "Next: Funder-ready check" : "Next slide"}
        showBack={true}
      />

      <PlayDeckModal
        open={playOpen}
        deck={deck}
        initialSlideNum={slide.num}
        onClose={() => setPlayOpen(false)}
      />
    </div>
  );
}


/* Two-step "Use different photo · 1 cr" shortcut, rendered under each
   sub-slide preview card. First click opens an inline chooser exposing:
     • Upload a photo · 1 cr → POST /custom-subslide-photo
     • AI-generate photo · 1 cr → POST /auto-photo?shortcut=true
   Both flows tag the resulting image with `source: 'custom_subslide'` so
   the preview/exporter treat it as a founder-picked photo and it survives
   the "user uploaded photo replaces AI" sweep.

   Why 1 credit for either path? Free upload / free AI regenerate exist
   inside the sub-slide editor (Photos & Diagrams section). The 1-credit
   charge here reflects the convenience of NOT having to leave the
   parent's preview page. */
function SubSlidePhotoAction({ deckId, slideNum, extraId, hasOwnPhoto, setDeck }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Two-state UI: closed shows the single "Use different photo" button.
  // Open shows the two-choice chooser (Upload | AI-generate). Auto-closes
  // when a choice is picked or the founder clicks outside.
  const [chooserOpen, setChooserOpen] = useState(false);

  // Shared post-success handler — both upload and AI-generate return the
  // same {image, cost_credits} shape and both drop prior auto photos.
  const applyImage = (image) => {
    setDeck((d) => ({
      ...d,
      slides: d.slides.map((s) => s.num !== slideNum ? s : {
        ...s,
        extras: (s.extras || []).map((x) => x.id !== extraId ? x : {
          ...x,
          images: [
            ...((x.images || []).filter((im) => im?.source !== "auto_generated" && im?.source !== "custom_subslide")),
            image,
          ],
        }),
      }),
    }));
    window.dispatchEvent(new Event("clinq:credits-changed"));
  };

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/pitch/decks/${deckId}/slide/${slideNum}/custom-subslide-photo?extra_id=${encodeURIComponent(extraId)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      applyImage(data.image);
      toast.success(`Sub-slide photo updated · ${data.cost_credits || 1} credit used`);
      setChooserOpen(false);
    } catch (e) {
      const detail = extractErrorMessage(e, "Photo upload failed");
      toast.error(typeof detail === "string" ? detail : "Photo upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onAiGenerate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Reuse the existing auto-photo endpoint with `shortcut=true` so the
      // pricing lives in ONE place (backend). Server charges the flat
      // 1-credit COST_CUSTOM_SUBSLIDE_PHOTO regardless of prior state and
      // tags the image with source='custom_subslide'.
      const { data } = await api.post(
        `/pitch/decks/${deckId}/slide/${slideNum}/auto-photo?shortcut=true&extra_id=${encodeURIComponent(extraId)}`
      );
      applyImage(data.image);
      toast.success(`AI photo generated · ${data.cost_credits || 1} credit used`);
      setChooserOpen(false);
    } catch (e) {
      const detail = extractErrorMessage(e, "AI photo generation failed");
      toast.error(typeof detail === "string" ? detail : "AI photo generation failed");
    } finally {
      setBusy(false);
    }
  };

  if (hasOwnPhoto) {
    return (
      <div
        className="mt-2 text-[11px] text-white/50 italic inline-flex items-center gap-1.5"
        data-testid={`subslide-custom-photo-active-${extraId}`}
      >
        <ImageIcon size={11} weight="fill" style={{ color: ORANGE }} />
        Custom photo applied — swap or delete from the sub-slide&apos;s Photos section.
      </div>
    );
  }

  if (chooserOpen) {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 flex-wrap"
           data-testid={`subslide-photo-chooser-${extraId}`}>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          style={{ display: "none" }}
          data-testid={`subslide-custom-photo-input-${extraId}`}
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={`subslide-upload-choice-${extraId}`}
          className="text-[11px] font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
          style={{
            backgroundColor: "rgba(245, 158, 11, 0.14)",
            color: ORANGE,
            border: "1px solid rgba(245, 158, 11, 0.40)",
          }}
          title="Upload your own image file for this sub-slide. 1 credit."
        >
          <ImageIcon size={11} weight="fill" />
          {busy ? "Uploading…" : "Upload photo · 1 cr"}
        </button>
        <button
          type="button"
          onClick={onAiGenerate}
          disabled={busy}
          data-testid={`subslide-ai-choice-${extraId}`}
          className="text-[11px] font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
          style={{
            backgroundColor: "rgba(245, 158, 11, 0.14)",
            color: ORANGE,
            border: "1px solid rgba(245, 158, 11, 0.40)",
          }}
          title="Generate a fresh AI photo tailored to this sub-slide's content. 1 credit."
        >
          <Sparkle size={11} weight="fill" />
          {busy ? "Generating…" : "Regenerate new photo · 1 cr"}
        </button>
        <button
          type="button"
          onClick={() => setChooserOpen(false)}
          disabled={busy}
          data-testid={`subslide-photo-chooser-cancel-${extraId}`}
          className="text-[11px] text-white/45 hover:text-white/70 underline underline-offset-2 disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setChooserOpen(true)}
      data-testid={`subslide-custom-photo-btn-${extraId}`}
      className="mt-2 text-[11px] font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors"
      style={{
        backgroundColor: "rgba(245, 158, 11, 0.10)",
        color: ORANGE,
        border: "1px solid rgba(245, 158, 11, 0.35)",
      }}
      title="Upload your own photo OR AI-generate a new one for this sub-slide. Either way costs 1 credit."
    >
      <ImageIcon size={11} weight="fill" />
      Use different photo · 1 cr
    </button>
  );
}
